import type { Unsubscribe } from 'utils/events';
import { State } from 'utils/state';
import { userSession } from 'state/session';
import { getComparisonVehicle } from 'state/session/compare';
import logger from 'utils/log';

export type TEnergyLabelSize = 'minimized' | 'maximized';

export interface IEnergyLabelState {
  labelSizes: Record<string, TEnergyLabelSize>;
  integratorUrls: Record<string, string>;
  fallbackActive: boolean;
}

class EnergyLabelState extends State<IEnergyLabelState> {
  private readonly unsubscribeSession: Unsubscribe;

  constructor() {
    super({
      labelSizes: {},
      integratorUrls: {},
      fallbackActive: false,
    });

    this.unsubscribeSession = userSession().on('change', (changedKeys: string[]) => {
      if (changedKeys.includes('activeVehicleId')) {
        const vehicleId = userSession().get('activeVehicleId');
        if (vehicleId) {
          this.setSizeInternal(vehicleId, 'maximized');
        }
        this.setValues({ fallbackActive: false });
      }

      if (changedKeys.includes('prString')) {
        const activeVehicleId = userSession().get('activeVehicleId');
        if (activeVehicleId) {
          this.setSizeInternal(activeVehicleId, 'maximized');
        }
        this.setValues({ fallbackActive: false });
      }

      if (changedKeys.includes('comparisonMode') && userSession().get('comparisonMode') === 'on') {
        const activeVehicleId = userSession().get('activeVehicleId');
        const [comparingVehicleId] = getComparisonVehicle();

        const sizes = { ...this.get('labelSizes') };
        if (activeVehicleId) {
          sizes[activeVehicleId] = 'minimized';
        }
        if (comparingVehicleId) {
          sizes[comparingVehicleId] = 'minimized';
        }
        this.setValues({ labelSizes: sizes });
      }
    });
  }

  dispose(): void {
    this.unsubscribeSession();
  }

  /**
   * Returns the label size for the given vehicle.
   * Defaults to 'maximized' if no size has been explicitly set.
   */
  getSize(vehicleId: string): TEnergyLabelSize {
    return this.get('labelSizes')[vehicleId] ?? 'maximized';
  }

  /**
   * Sets the label size for a specific vehicle.
   * Two-state transition: maximized↔minimized both directions.
   * Same-state set is a no-op.
   */
  setSize(vehicleId: string, size: TEnergyLabelSize): void {
    const current = this.getSize(vehicleId);
    if (current === size) {
      return;
    }
    this.setSizeInternal(vehicleId, size);
  }

  private setSizeInternal(vehicleId: string, size: TEnergyLabelSize): void {
    const sizes = { ...this.get('labelSizes') };
    sizes[vehicleId] = size;
    this.setValues({ labelSizes: sizes });
  }

  /**
   * Store an integrator-provided image URL.
   *
   * Two-argument form: explicitly associate a URL with a specific vehicle ID.
   *   setIntegratorUrl(vehicleId, url)
   *   setIntegratorUrl(vehicleId, '') — removes the entry
   *
   * One-argument form: associate the URL with the currently active vehicle.
   *   setIntegratorUrl(url)
   *   setIntegratorUrl('') — removes the entry for the active vehicle
   */
  setIntegratorUrl(url: string): void;
  setIntegratorUrl(vehicleId: string, url: string): void;
  setIntegratorUrl(vehicleIdOrUrl: string, url?: string): void {
    if (url !== undefined) {
      // Two-argument form: vehicleIdOrUrl is the vehicleId
      this.applyIntegratorUrl(vehicleIdOrUrl, url);
    } else {
      // One-argument form: vehicleIdOrUrl is the URL; vehicleId comes from session
      const activeVehicleId = userSession().get('activeVehicleId');
      if (!activeVehicleId) {
        logger.warn('[EnergyLabel] setIntegratorUrl called with no activeVehicleId — URL ignored.');
        return;
      }
      this.applyIntegratorUrl(activeVehicleId, vehicleIdOrUrl);
    }
  }

  private applyIntegratorUrl(vehicleId: string, url: string): void {
    const current = { ...this.get('integratorUrls') };
    if (url === '') {
      delete current[vehicleId];
    } else {
      current[vehicleId] = url;
    }
    this.setValues({ integratorUrls: current });
  }

  /**
   * Returns the integrator-provided image URL for the given vehicle ID,
   * or null if no URL has been stored for that vehicle.
   */
  getImageUrl(vehicleId: string): string | null {
    const urls = this.get('integratorUrls');
    return urls[vehicleId] ?? null;
  }

  /**
   * Sets the session-scoped fallback flag.
   * When true, the energy label component should not render
   * (the label image could not be resolved).
   */
  setFallbackActive(active: boolean): void {
    this.setValues({ fallbackActive: active });
  }
}

let instance: EnergyLabelState | undefined;

export function energyLabelState(): EnergyLabelState {
  if (!instance) {
    instance = new EnergyLabelState();
  }
  return instance;
}

/**
 * Resets the singleton for testing purposes.
 * This function is exported only for use in unit tests.
 */
export function resetEnergyLabelState(): void {
  instance?.dispose();
  instance = undefined;
}
